<!--
  事件选项面板组件
  职责：以分类侧栏 + 文字按钮列表的形式，让用户向当前场景插入新事件
  数据来源：eventPalette 配置 + sceneEditor store
-->
<script setup lang="ts">
import { ref, computed } from 'vue'
import {
  NButton,
  NModal,
  NScrollbar,
  useMessage,
} from 'naive-ui'
import EventPaletteActionButton from '@/components/workspace/EventPaletteActionButton.vue'
import VariableBatchOperationModal from '@/components/workspace/VariableBatchOperationModal.vue'
import { ENABLE_CUSTOM_UI_EDITOR, ENABLE_STORY_LOCK_EVENT } from '@/config/featureFlags'
import { collectInheritedImageLayers, type InheritedImageLayer } from '@/components/eventEditors/imageLayerPreview'
import { EVENT_PALETTE, type EventPaletteItem } from '@/events/palette/eventPalette'
import { resolveResourceUrlFromFileName } from '@/features/virtual-resource/fileNamePath'
import EventDisposePic from '@/model/EventClass/EventDisposePic'
import EventID from '@/model/EventClass/EventID'
import { useSceneEditorStore } from '@/store/sceneEditor'
import { useWorkspaceStore } from '@/store/workspace'

const message = useMessage()
const editor = useSceneEditorStore()
const workspace = useWorkspaceStore()
const variableBatchModalOpen = ref(false)
const disposeLayerModalOpen = ref(false)
const selectedDisposeLayerIds = ref(new Set<number>())
const paletteToolKey = ref('message')

type PaletteShortcutChild = {
  key: string
  label: string
  item: EventPaletteItem
  layer?: 'far' | 'background' | 'portrait' | 'foreground'
}

type PaletteShortcutEntry = {
  key: string
  label: string
  accentClass: string
  item?: EventPaletteItem
  children?: PaletteShortcutChild[]
}

type DisposableLayerRow = InheritedImageLayer & {
  pathLabel: string
  previewUrl: string
  selected: boolean
}

const allPaletteItems = computed(() => EVENT_PALETTE.flatMap((category) => category.items))

function findPaletteItem(label: string): EventPaletteItem {
  const item = allPaletteItems.value.find((current) => current.label === label)
  if (!item) {
    throw new Error(`事件快捷入口缺少配置：${label}`)
  }
  return item
}

const shortcutEntries = computed<PaletteShortcutEntry[]>(() => [
  {
    key: 'message',
    label: '消息',
    accentClass: 'palette-category-accent--msg',
    children: [
      { key: 'message-text', label: '显示文本', item: findPaletteItem('显示文本') },
      { key: 'message-text-off', label: '消失对话框', item: findPaletteItem('消失对话框') },
      { key: 'message-auto', label: '自动播放', item: findPaletteItem('自动播放剧情') },
      { key: 'message-comment', label: '注释', item: findPaletteItem('注释') },
    ],
  },
  {
    key: 'choice',
    label: '选项',
    accentClass: 'palette-category-accent--choice',
    children: [
      { key: 'text-choice', label: '文字选项', item: findPaletteItem('文字选项') },
      { key: 'image-choice', label: '图片选项', item: findPaletteItem('图片选项') },
    ],
  },
  {
    key: 'variable',
    label: '变量',
    accentClass: 'palette-category-accent--logic',
    children: [
      { key: 'var-batch', label: '变量操作', item: findPaletteItem('变量操作') },
      { key: 'var-number', label: '数值', item: findPaletteItem('数值操作') },
      { key: 'var-ex', label: '二周目', item: findPaletteItem('二周目数值') },
      { key: 'var-string', label: '字符串', item: findPaletteItem('字符串') },
    ],
  },
  {
    key: 'image',
    label: '图像',
    accentClass: 'palette-category-accent--image',
    children: [
      { key: 'image-far', label: '远景图', item: findPaletteItem('显示远景图'), layer: 'far' },
      { key: 'image-background', label: '背景图', item: findPaletteItem('显示背景图'), layer: 'background' },
      { key: 'image-foreground', label: '前景图', item: findPaletteItem('显示前景图'), layer: 'foreground' },
      { key: 'image-portrait', label: '立绘', item: findPaletteItem('显示立绘'), layer: 'portrait' },
      { key: 'image-off', label: '消除', item: findPaletteItem('图片消除') },
      { key: 'image-move', label: '移动图片', item: findPaletteItem('图片移动') },
      { key: 'image-rotate', label: '旋转图片', item: findPaletteItem('图片旋转') },
    ],
  },
  {
    key: 'audio',
    label: '音频',
    accentClass: 'palette-category-accent--av',
    children: [
      { key: 'audio-bgm', label: '音乐', item: findPaletteItem('播放背景音乐') },
      { key: 'audio-se', label: '音效', item: findPaletteItem('播放音效') },
      { key: 'audio-bgs', label: '循环音', item: findPaletteItem('播放循环音效') },
      { key: 'audio-voice', label: '语音', item: findPaletteItem('播放语音') },
      { key: 'audio-fade-bgm', label: '淡出音乐', item: findPaletteItem('淡出背景音乐') },
      { key: 'audio-fade-se', label: '淡出音效', item: findPaletteItem('淡出音效') },
      { key: 'audio-fade-bgs', label: '淡出循环音', item: findPaletteItem('淡出循环音效') },
      { key: 'audio-fade-voice', label: '淡出语音', item: findPaletteItem('淡出语音') },
    ],
  },
  {
    key: 'effect',
    label: '特效',
    accentClass: 'palette-category-accent--effect',
    children: [
      { key: 'effect-wait', label: '等待', item: findPaletteItem('等待') },
      { key: 'effect-weather', label: '天气', item: findPaletteItem('天气') },
      { key: 'effect-flash', label: '闪烁', item: findPaletteItem('闪烁') },
      { key: 'effect-shake', label: '震动', item: findPaletteItem('震动') },
    ],
  },
  {
    key: 'logic',
    label: '逻辑',
    accentClass: 'palette-category-accent--logic',
    children: [
      { key: 'condition', label: '条件分歧', item: findPaletteItem('条件分歧') },
      { key: 'loop', label: '循环', item: findPaletteItem('循环') },
      { key: 'loop-break', label: '中断循环', item: findPaletteItem('中断循环') },
      { key: 'save-read', label: '存读档', item: findPaletteItem('存读档') },
      { key: 'jump', label: '跳转剧情', item: findPaletteItem('跳转剧情') },
      { key: 'call', label: '呼叫剧情', item: findPaletteItem('呼叫子剧情') },
      { key: 'title', label: '返回标题', item: findPaletteItem('返回标题画面') },
      ...(ENABLE_STORY_LOCK_EVENT
        ? [{ key: 'story-lock', label: '剧情锁', item: findPaletteItem('剧情锁') }]
        : []),
    ],
  },
  ...(ENABLE_CUSTOM_UI_EDITOR ? [{
    key: 'ui',
    label: 'UI界面',
    accentClass: 'palette-category-accent--ui',
    children: [
      { key: 'open-ui', label: '打开界面', item: findPaletteItem('打开界面') },
      { key: 'close-ui', label: '关闭界面', item: findPaletteItem('关闭界面') },
    ],
  }] : []),
])

const activeTextEntry = computed(() =>
  shortcutEntries.value.find((entry) => entry.key === paletteToolKey.value) ?? shortcutEntries.value[0] ?? null,
)

const activeTextItems = computed<PaletteShortcutChild[]>(() => {
  const entry = activeTextEntry.value
  if (!entry) return []
  if (entry.children?.length) return filterShortcutChildren(entry.children)
  if (!entry.item) return []
  if (!canUsePaletteItem(entry.item)) return []
  return [{
    key: entry.key,
    label: entry.label,
    item: entry.item,
  }]
})

const disposeInsertBoundary = computed(() => {
  const placeholder = editor.selectedAddPlaceholder
  if (placeholder) return placeholder.insertAt
  if (editor.selectedEventIndex === null) return editor.selectedEventList.length
  return editor.selectedEventIndex
})

const disposableLayers = computed<InheritedImageLayer[]>(() =>
  collectInheritedImageLayers(editor.selectedEventList, disposeInsertBoundary.value),
)

const disposableLayerRows = computed<DisposableLayerRow[]>(() =>
  disposableLayers.value.map((layer) => ({
    ...layer,
    pathLabel: getDisposeLayerPathLabel(layer.path),
    previewUrl: resolveResourceUrlFromFileName(
      workspace.virtualResourceRoots.graphic,
      'graphic',
      layer.path,
      workspace.resourcePreviewOverrides,
    ),
    selected: selectedDisposeLayerIds.value.has(layer.layerId),
  })),
)

function canUsePaletteItem(item: EventPaletteItem): boolean {
  if (item.availability === 'custom-ui-button-click') {
    return ENABLE_CUSTOM_UI_EDITOR && editor.selectedCustomUi?.eventSource === 'control'
  }
  return true
}

function filterShortcutChildren(children: PaletteShortcutChild[]): PaletteShortcutChild[] {
  return children.filter((child) => canUsePaletteItem(child.item))
}

/** 点击按钮时在当前场景插入事件 */
function onInsertEvent(item: EventPaletteItem) {
  const hasEventTarget = !!editor.selectedMap || (ENABLE_CUSTOM_UI_EDITOR && editor.selectedCustomUi?.eventSource !== 'none')
  if (!hasEventTarget) {
    message.warning(ENABLE_CUSTOM_UI_EDITOR ? '请选择左侧剧情或 UI 事件节点' : '请选择左侧剧情')
    return
  }
  if (item.action === 'open-variable-batch') {
    variableBatchModalOpen.value = true
    return
  }
  if (item.eventCode === EventID.DisposePic) {
    openDisposeLayerModal()
    return
  }
  if (!canUsePaletteItem(item)) {
    message.warning(ENABLE_CUSTOM_UI_EDITOR ? '关闭界面只能添加到 UI 按钮点击事件' : '当前暂不支持添加该事件')
    return
  }
  const ev = editor.insertEventFromPalette(item)
  if (!ev) {
    message.warning('当前位置不能添加该事件')
    return
  }
}

function openDisposeLayerModal() {
  const layers = disposableLayers.value
  if (!layers.length) {
    message.warning('当前位置之前没有可消除的图片图层')
    return
  }
  selectedDisposeLayerIds.value = new Set()
  disposeLayerModalOpen.value = true
}

function toggleDisposeLayer(layerId: number) {
  const next = new Set(selectedDisposeLayerIds.value)
  if (next.has(layerId)) {
    next.delete(layerId)
  } else {
    next.add(layerId)
  }
  selectedDisposeLayerIds.value = next
}

function selectAllDisposeLayers() {
  selectedDisposeLayerIds.value = new Set(disposableLayers.value.map((item) => item.layerId))
}

function clearDisposeLayers() {
  selectedDisposeLayerIds.value = new Set()
}

function getDisposeLayerPathLabel(path: string): string {
  const normalized = path.replace(/\\/g, '/')
  return normalized.split('/').filter(Boolean).pop() ?? path
}

function confirmDisposeLayers() {
  const layerIds = disposableLayers.value
    .map((item) => item.layerId)
    .filter((layerId) => selectedDisposeLayerIds.value.has(layerId))
    .sort((a, b) => a - b)
  if (!layerIds.length) {
    message.warning('请选择至少一个要消除的图层')
    return
  }
  const events = layerIds.map((layerId) => {
    const event = new EventDisposePic()
    event.PicId = layerId
    return event
  })
  const inserted = editor.insertEventObjects(events, '批量消除图片')
  if (!inserted?.length) {
    message.warning('当前位置不能添加图片消除事件')
    return
  }
  disposeLayerModalOpen.value = false
}

function onVariableBatchInserted() {
  variableBatchModalOpen.value = false
}
</script>

<template>
  <div class="palette-pane">
    <div class="palette-layout">
      <div class="palette-sidebar">
        <button
          v-for="entry in shortcutEntries"
          :key="entry.key"
          type="button"
          class="palette-category-btn palette-tool-category-btn"
          :class="[
            entry.accentClass,
            { 'palette-category-btn--active': paletteToolKey === entry.key },
          ]"
          :title="entry.label"
          @click="paletteToolKey = entry.key"
        >
          <span class="palette-category-btn__text">{{ entry.label }}</span>
        </button>
      </div>

      <n-scrollbar class="palette-scroll">
        <div class="palette-buttons">
          <EventPaletteActionButton
            v-for="item in activeTextItems"
            :key="item.key"
            :label="item.label"
            :show-icon="false"
            @click="onInsertEvent(item.item)"
          />
        </div>
      </n-scrollbar>
    </div>
  </div>
  <VariableBatchOperationModal
    v-model:show="variableBatchModalOpen"
    @inserted="onVariableBatchInserted"
  />
  <n-modal
    v-model:show="disposeLayerModalOpen"
    preset="card"
    title="选择要消除的图层"
    class="dispose-layer-modal"
    :mask-closable="false"
  >
    <div class="dispose-layer-panel">
      <div class="dispose-layer-toolbar">
        <span class="dispose-layer-count">
          已选 {{ selectedDisposeLayerIds.size }} / {{ disposableLayerRows.length }}
        </span>
        <div class="dispose-layer-toolbar-actions">
          <n-button size="small" tertiary @click="selectAllDisposeLayers">全选</n-button>
          <n-button size="small" tertiary @click="clearDisposeLayers">全不选</n-button>
        </div>
      </div>
      <div class="dispose-layer-list">
        <label
          v-for="layer in disposableLayerRows"
          :key="layer.layerId"
          class="dispose-layer-row"
        >
          <input
            type="checkbox"
            class="dispose-layer-checkbox"
            :checked="layer.selected"
            @change="toggleDisposeLayer(layer.layerId)"
          />
          <span class="dispose-layer-preview">
            <img
              v-if="layer.previewUrl"
              :src="layer.previewUrl"
              :alt="`图层 ${layer.layerId} 预览`"
              class="dispose-layer-preview-image"
              draggable="false"
            />
          </span>
          <span class="dispose-layer-info">
            <span class="dispose-layer-id">图层 {{ layer.layerId }}</span>
            <span class="dispose-layer-path" :title="layer.path">{{ layer.pathLabel }}</span>
          </span>
        </label>
      </div>
      <div class="dispose-layer-actions">
        <n-button @click="disposeLayerModalOpen = false">取消</n-button>
        <n-button type="primary" @click="confirmDisposeLayers">确定</n-button>
      </div>
    </div>
  </n-modal>
</template>

<style scoped>
.palette-pane {
  --palette-tab-active-bg: var(--theme-bg-panel);
  flex-shrink: 0;
  width: 110px;
  min-width: 110px;
  max-width: 132px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  border-right: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
}

:global(.dispose-layer-modal) {
  width: min(420px, calc(100vw - 24px));
  background: var(--theme-bg-panel);
}

.dispose-layer-panel {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  gap: var(--app-space-sm);
  max-height: min(560px, calc(100vh - 128px));
  max-height: min(560px, calc(100dvh - 128px));
  min-height: 240px;
  background: var(--theme-bg-panel);
}

.dispose-layer-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--app-space-sm);
  padding-bottom: var(--app-space-xs);
  border-bottom: 1px solid var(--theme-border);
}

.dispose-layer-count {
  font-size: var(--app-font-size-sm);
  color: var(--theme-text-secondary);
}

.dispose-layer-toolbar-actions {
  display: flex;
  gap: var(--app-space-xs);
}

.dispose-layer-list {
  min-height: 0;
  overflow: auto;
  background: var(--theme-bg-panel);
}

.dispose-layer-row {
  display: grid;
  grid-template-columns: auto 56px minmax(0, 1fr);
  align-items: center;
  gap: var(--app-space-sm);
  min-height: 62px;
  padding: var(--app-space-xs);
  border-bottom: 1px solid var(--theme-border);
  color: var(--theme-text);
  cursor: pointer;
}

.dispose-layer-row:hover {
  background: var(--theme-bg-hover);
}

.dispose-layer-checkbox {
  width: 14px;
  height: 14px;
  margin: 0;
}

.dispose-layer-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 48px;
  overflow: hidden;
  background: var(--theme-bg-card);
}

.dispose-layer-preview-image {
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.dispose-layer-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 3px;
}

.dispose-layer-id {
  font-size: var(--app-font-size-sm);
  color: var(--theme-text);
}

.dispose-layer-path {
  overflow: hidden;
  font-size: var(--app-font-size-sm);
  color: var(--theme-text-secondary);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dispose-layer-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--app-space-sm);
  padding-top: var(--app-space-xs);
  border-top: 1px solid var(--theme-border);
}

@media (orientation: portrait) and (max-width: 1180px) {
  :global(.dispose-layer-modal) {
    width: min(400px, calc(100vw - 16px));
    transform: translateY(-32px);
  }

  .dispose-layer-panel {
    max-height: calc(100vh - 176px);
    max-height: calc(100dvh - 176px);
  }

  .dispose-layer-actions {
    padding-bottom: max(44px, env(safe-area-inset-bottom));
  }
}

.palette-layout {
  display: flex;
  flex: 1;
  min-height: 0;
}

.palette-sidebar {
  display: flex;
  flex: 0 0 24px;
  flex-direction: column;
  gap: 1px;
  padding: 1px 0 1px 2px;
  border-right: 0;
  background: var(--theme-bg-raised);
  box-sizing: border-box;
}

.palette-category-btn {
  width: 100%;
  min-height: 42px;
  padding: 1px 0;
  border: 0;
  background: var(--theme-bg-raised);
  color: var(--theme-text-secondary);
  writing-mode: vertical-rl;
  text-orientation: mixed;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-sizing: border-box;
  margin-right: 0;
}

.palette-category-btn__text {
  font-size: 11px;
  line-height: 1;
  letter-spacing: 0.3px;
}

.palette-category-btn--active {
  color: var(--theme-text-heading);
  position: relative;
  z-index: 1;
}

.palette-tool-category-btn {
  color: var(--palette-accent, var(--theme-text-secondary));
}

.palette-sidebar .palette-category-btn.palette-category-btn--active {
  background-color: var(--palette-tab-active-bg) !important;
}

.palette-tool-category-btn:hover {
  background: var(--theme-bg-hover);
}

.palette-category-accent--msg {
  --palette-accent: #6f9fe8;
  --palette-accent-strong: #9fc0f3;
}

.palette-category-accent--choice {
  --palette-accent: #5da6d7;
  --palette-accent-strong: #80bee8;
}

.palette-category-accent--logic {
  --palette-accent: #a88be8;
  --palette-accent-strong: #c2adf3;
}

.palette-category-accent--effect {
  --palette-accent: #d7a85d;
  --palette-accent-strong: #ebc27d;
}

.palette-category-accent--image {
  --palette-accent: #66bf9c;
  --palette-accent-strong: #86d5b8;
}

.palette-category-accent--av {
  --palette-accent: #dd7f90;
  --palette-accent-strong: #f0a0ae;
}

.palette-category-accent--ui {
  --palette-accent: #67a6d8;
  --palette-accent-strong: #8cc3ec;
}

.palette-scroll {
  flex: 1;
  min-height: 0;
}

.palette-buttons {
  padding: 3px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  box-sizing: border-box;
}

@media (max-width: 900px) and (orientation: landscape) {
  .palette-pane {
    width: 100% !important;
    max-width: none;
    min-height: 0;
    max-height: 38vh;
    border-right: 0;
    border-bottom: 1px solid var(--theme-border);
  }

  .palette-sidebar {
    flex: 0 0 34px;
  }

  .palette-scroll {
    max-height: 38vh;
  }
}
</style>
